// "Sync with another device": pair two devices by QR code (or a pasted code) and merge their trees
// directly over the local network. See src/sync/session.ts and src/model/sync.ts.

import { Camera, Check, ChevronLeft, ChevronRight, Copy, Plus, QrCode, RefreshCw, Share } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { isMemoryOnly } from '../../model/db';
import { TYPE_LABELS, type ItemChange, type SidePreview } from '../../model/sync';
import { CODE_PLACEHOLDER, decodeSignal, deviceName, signalToText, textToSignal, type SignalType } from '../../sync/codes';
import { encodeQr, qrPath } from '../../sync/qr';
import { forgetDevice, getPairs, getPeers, SyncSession, type Pair, type Peer, type Preview, type Summary, type SyncCallbacks, type SyncError } from '../../sync/session';
import { plural } from '../labels';
import { closeDialog, getState, openDialog, toast, updateSettings, useApp } from '../store';
import { Modal } from './common';

/** Entry point for every Sync button: explains why not, when sync can't run here. */
export function startSync() {
  if (typeof RTCPeerConnection === 'undefined') return toast('This browser doesn’t support direct connections between devices.', 'error');
  if (isMemoryOnly()) return toast('This browser isn’t allowing storage, so there’s nowhere to keep synced trees.', 'error');
  // Already open (the command palette opens over it): a second one would start a second sync.
  if (getState().dialogs.some((d) => d.type === 'sync')) return;
  openDialog({ type: 'sync' });
}

const CONNECT_FAILED = 'Couldn’t connect. Make sure both devices are on the same Wi-Fi network with this app open, then try again.';

const cameraSupported = () => typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia && window.isSecureContext;

type Step =
  | { k: 'choose' }
  | { k: 'preparing'; note?: string }
  | { k: 'offer'; bytes: Uint8Array }
  | { k: 'reconnect'; bytes: Uint8Array; name: string }
  | { k: 'answer' }
  | { k: 'reply'; bytes: Uint8Array }
  | { k: 'connecting'; name: string }
  | { k: 'addresses'; bytes: Uint8Array; name: string }
  | { k: 'preview'; preview: Preview }
  | { k: 'done'; summary: Summary }
  | { k: 'canceled' }
  | { k: 'failed'; message: string; details: string };

// Keep the screen on while the dialog is open: a phone that locks itself (iOS Auto-Lock is 30 s by
// default) suspends the page and cuts the connection, typically while the person is reviewing the
// preview on the other device.
function useWakeLock() {
  useEffect(() => {
    let lock: WakeLockSentinel | null = null;
    let on = true;
    const request = async () => {
      try {
        if (on && !lock && navigator.wakeLock && document.visibilityState === 'visible') {
          lock = await navigator.wakeLock.request('screen');
          lock.addEventListener('release', () => (lock = null));
          if (!on) void lock.release();
        }
      } catch {
        /* unsupported or refused: "How sync works" still says to keep the screen on */
      }
    };
    const visible = () => void request();
    void request();
    document.addEventListener('visibilitychange', visible);
    return () => {
      on = false;
      document.removeEventListener('visibilitychange', visible);
      void lock?.release().catch(() => {});
    };
  }, []);
}

export function SyncDialog() {
  const [step, setStep] = useState<Step>({ k: 'choose' });
  const [status, setStatus] = useState('');
  const [busy, setBusy] = useState(false);
  const cameraForCodes = useApp((s) => !!s.settings.syncCameraForCodes);
  const ctx = useRef<{ session: SyncSession | null; timer?: ReturnType<typeof setTimeout>; fallback?: ReturnType<typeof setTimeout> }>({ session: null });
  useWakeLock();

  const clearTimers = () => {
    clearTimeout(ctx.current.timer);
    clearTimeout(ctx.current.fallback);
  };

  // Closing while the preview is up counts as "Cancel" on both devices.
  useEffect(
    () => () => {
      clearTimers();
      const s = ctx.current.session;
      if (s && !s.finished) void (s.confirmed ? s.close() : s.cancel());
    },
    [],
  );

  async function fail(e: SyncError) {
    clearTimers();
    const s = ctx.current.session;
    // A session that failed on its own closes itself, after telling the other device why.
    const details = e.details || (s && !s.finished ? await s.details() : '');
    if (s && !s.finished) s.close();
    console.error(e, details);
    setStep({ k: 'failed', message: e.message || 'Sync failed.', details });
  }

  const callbacks = (): Partial<SyncCallbacks> => ({
    onConnected: clearTimers,
    onProgress: setStatus,
    onError: (e) => void fail(e),
    onDone: (summary) => {
      clearTimers();
      setStep({ k: 'done', summary });
      toast('Sync complete.', 'success');
    },
    onPreview: (preview) => {
      clearTimers();
      setBusy(false);
      setStatus('');
      setStep({ k: 'preview', preview });
    },
    onConfirmed: () => {
      setBusy(true);
      setStatus('Confirmed on the other device. Syncing…');
    },
    onCanceled: () => {
      clearTimers();
      setStep({ k: 'canceled' });
    },
  });

  async function newSession(opts?: { keepCert?: boolean }) {
    // An earlier attempt's timers would otherwise fail this one ("Pair again with two codes" from
    // the one-scan screen kept its 3-minute limit).
    clearTimers();
    ctx.current.session?.close();
    const s = await SyncSession.create(callbacks(), opts);
    ctx.current.session = s;
    return s;
  }

  function choose() {
    clearTimers();
    const s = ctx.current.session;
    if (s && !s.finished) s.close();
    ctx.current.session = null;
    setStatus('');
    setBusy(false);
    setStep({ k: 'choose' });
  }

  // Device 1: show the offer, then read the reply.
  async function startOffer() {
    setStatus('');
    setStep({ k: 'preparing' });
    try {
      const s = await newSession();
      setStep({ k: 'offer', bytes: await s.createOffer() });
    } catch (e) {
      void fail(e as SyncError);
    }
  }

  async function onReply(reply: Uint8Array) {
    setStatus('Connecting…');
    await ctx.current.session!.acceptAnswer(reply);
    ctx.current.timer = setTimeout(() => void fail(new Error(CONNECT_FAILED)), 25000);
  }

  // A remembered device: show one code, and it connects without a reply.
  async function startReconnect(peerId: string, name: string) {
    setStatus('');
    setStep({ k: 'preparing' });
    try {
      // Browsers share only a private .local name for this device unless the page is using the
      // camera (or, in Chrome, has camera permission), and other devices often can't look that
      // name up. Then the other device falls back to showing its own address, which this screen
      // accepts. Only with the opt-in setting is the camera switched on for a moment while the
      // code is made, so the code carries this device's real address and one scan is enough.
      let cam: MediaStream | null = null;
      if (cameraForCodes && cameraSupported()) {
        setStep({ k: 'preparing', note: 'Switching the camera on for a moment so the other device can find this one (you turned this on in Settings). Nothing is recorded. Your browser may ask for permission.' });
        cam = await navigator.mediaDevices.getUserMedia({ video: true, audio: false }).catch(() => null);
      }
      const s = await newSession();
      s.usedCamera = !!cam;
      let bytes: Uint8Array;
      try {
        bytes = await s.createReconnectOffer(peerId);
      } finally {
        cam?.getTracks().forEach((t) => t.stop());
      }
      setStatus(`Waiting for ${name}…`);
      setStep({ k: 'reconnect', bytes, name });
      ctx.current.timer = setTimeout(() => void fail(new Error(CONNECT_FAILED)), 180000);
    } catch (e) {
      void fail(e as SyncError);
    }
  }

  async function onAddresses(addrs: Uint8Array) {
    setStatus('Connecting…');
    await ctx.current.session!.addRemoteAddresses(addrs);
  }

  // Device 2: read the offer, then show the reply.
  async function onOffer(offer: Uint8Array) {
    const sig = decodeSignal(offer);
    const reconnect = sig.type === 'reconnect';
    setStatus(reconnect ? 'Connecting…' : 'Preparing reply…');
    const s = await newSession({ keepCert: reconnect });
    // The camera is still running here on purpose: Safari only shares local network addresses
    // with a page that's using the camera, and the reply needs them. (The scanner stops when the
    // step below replaces it.)
    const reply = await s.acceptOffer(offer);
    if (reply) {
      setStatus('Waiting for the other device…');
      setStep({ k: 'reply', bytes: reply });
      ctx.current.timer = setTimeout(() => void fail(new Error(CONNECT_FAILED)), 180000);
      return;
    }
    // For a reconnect, also read this device's addresses now, before the camera stops: Safari on
    // iPhone hides its real addresses again once the camera is off.
    const addrCode = await s.addressCode().catch((e: Error) => e);
    const name = sig.type === 'reconnect' ? (await getPairs())[sig.from]?.name || 'the other device' : 'the other device';
    setStep({ k: 'connecting', name });
    // If the other device's code only had a .local name this one can't look up, nothing arrives.
    // Then show this device's own addresses for it to scan or paste.
    ctx.current.fallback = setTimeout(() => {
      if (ctx.current.session !== s || s.finished || s.opened) return;
      if (addrCode instanceof Error) return void fail(addrCode);
      setStatus(`Waiting for ${name}…`);
      setStep({ k: 'addresses', bytes: addrCode, name });
    }, 6000);
    ctx.current.timer = setTimeout(() => void fail(new Error(CONNECT_FAILED)), 180000);
  }

  async function confirm() {
    setBusy(true);
    setStatus('Syncing…');
    try {
      await ctx.current.session!.confirm();
    } catch (e) {
      void fail(e as SyncError);
    }
  }

  const back = (
    <button className="btn small ghost sync-back" onClick={choose}>
      <ChevronLeft size={15} /> Back
    </button>
  );
  const statusLine = (
    <p className="faint small-text sync-status" aria-live="polite">
      {status}
    </p>
  );

  let content: ReactNode;
  switch (step.k) {
    case 'choose':
      content = <Choose onOffer={startOffer} onScan={() => setStep({ k: 'answer' })} onReconnect={startReconnect} />;
      break;
    case 'preparing':
      content = (
        <>
          <p>Preparing…</p>
          {step.note && <p className="faint small-text">{step.note}</p>}
        </>
      );
      break;
    case 'offer':
      content = (
        <>
          {back}
          <p>
            <b>Step 1.</b> On your other device, open Sync, choose “Scan code”, and scan this:
          </p>
          <CodeBlock bytes={step.bytes} />
          <p>
            <b>Step 2.</b> The other device then shows a reply code. Scan it or paste it here:
          </p>
          <CodeReader scanLabel="Scan reply" expect={['answer']} onCode={onReply} onError={fail} />
          {statusLine}
        </>
      );
      break;
    case 'reconnect':
      content = (
        <>
          {back}
          <p>On {step.name}, open Sync and choose “Scan code”, then scan this:</p>
          <CodeBlock bytes={step.bytes} />
          {statusLine}
          <div className="sync-other">
            <p className="faint small-text">If {step.name} shows a code back, scan it or paste it here:</p>
            <CodeReader scanLabel="Scan its code" expect={['addresses']} primary={false} onCode={onAddresses} onError={fail} />
          </div>
          <p className="faint small-text">
            Still not connecting?{' '}
            <button className="btn small ghost" onClick={startOffer}>
              Pair again with two codes
            </button>
          </p>
          <LiveDetails session={ctx.current.session} />
        </>
      );
      break;
    case 'answer':
      content = (
        <>
          {back}
          <p>Scan the code shown on your other device, or paste it:</p>
          <CodeReader scanLabel="Scan code" expect={['offer', 'reconnect']} autoScan onCode={onOffer} onError={fail} />
          {statusLine}
        </>
      );
      break;
    case 'reply':
      content = (
        <>
          {back}
          <p>Now show this to the other device. Scan it there with “Scan reply”, or copy the code and paste it there:</p>
          <CodeBlock bytes={step.bytes} />
          {statusLine}
        </>
      );
      break;
    case 'connecting':
      content = (
        <>
          <p>Connecting to {step.name}…</p>
          {statusLine}
          <LiveDetails session={ctx.current.session} />
        </>
      );
      break;
    case 'addresses':
      content = (
        <>
          <p>
            One more step: {step.name} can’t be reached directly on this Wi-Fi. On {step.name}, scan this code, or copy it and paste it there:
          </p>
          <CodeBlock bytes={step.bytes} />
          {statusLine}
          <LiveDetails session={ctx.current.session} />
        </>
      );
      break;
    case 'preview':
      content = (
        <>
          <p>Here’s what syncing will change. Nothing has changed yet. You can confirm on either device.</p>
          <div className="sync-preview">
            <PreviewSide title={`This device (${deviceName()})`} side={step.preview.here} />
            <PreviewSide title={`Other device (${step.preview.device || 'unknown'})`} side={step.preview.there} />
          </div>
          <div className="row wrap">
            <button className="btn primary" disabled={busy} onClick={confirm}>
              <RefreshCw size={15} /> Sync
            </button>
            <button className="btn" disabled={busy} onClick={closeDialog}>
              Cancel
            </button>
          </div>
          {statusLine}
        </>
      );
      break;
    case 'done': {
      const s = step.summary;
      const parts = [
        s.added && `${plural(s.added, 'tree')} added`,
        s.changed && `${plural(s.changed, 'tree')} updated`,
        s.removed && `${plural(s.removed, 'tree')} deleted`,
        s.settings.length && (s.settings.length === 1 ? s.settings[0] : `${plural(s.settings.length, 'setting')} changed`),
      ].filter(Boolean);
      content = (
        <>
          <div className="sync-done">
            <Check size={26} />
            <p>
              <b>Sync complete.</b>{' '}
              {parts.length ? `On this device: ${parts.join(', ')}${s.files ? ` (with ${plural(s.files, 'media file')})` : ''}.` : 'This device was already up to date.'}
            </p>
          </div>
          <p className="faint small-text">Synced with {s.device || 'the other device'} just now.</p>
        </>
      );
      break;
    }
    case 'canceled':
      content = (
        <>
          <p>Sync was canceled. Nothing was changed on either device.</p>
          <div className="row">
            <button className="btn primary" onClick={choose}>
              Start over
            </button>
          </div>
        </>
      );
      break;
    case 'failed':
      content = (
        <>
          <p className="sync-error" role="alert">
            {step.message}
          </p>
          <p className="faint small-text">Nothing is lost: anything already copied is kept, and the next sync picks up where this one stopped.</p>
          {step.details && <Details text={step.details} />}
          <div className="row">
            <button className="btn primary" onClick={choose}>
              Start over
            </button>
          </div>
        </>
      );
      break;
  }

  return (
    <Modal
      title="Sync with another device"
      footer={
        <button className={`btn${step.k === 'done' ? ' primary' : ''}`} onClick={closeDialog}>
          {step.k === 'done' ? 'Done' : 'Close'}
        </button>
      }
    >
      <div className="sync stack">{content}</div>
    </Modal>
  );
}

function Choose({ onOffer, onScan, onReconnect }: { onOffer: () => void; onScan: () => void; onReconnect: (id: string, name: string) => void }) {
  const [known, setKnown] = useState<[string, Pair, Peer | undefined][] | null>(null);
  useEffect(() => {
    let live = true;
    Promise.all([getPairs(), getPeers()])
      .then(([pairs, peers]) => {
        // Only devices that can scan this one's one-scan code (see canEditIceCredentials).
        const list = Object.entries(pairs)
          .filter(([, p]) => p.canAnswer !== false)
          .map(([id, p]) => [id, p, peers[id]] as [string, Pair, Peer | undefined])
          .sort((a, b) => ((a[2]?.at ?? '') < (b[2]?.at ?? '') ? 1 : -1));
        if (live) setKnown(list);
      })
      .catch(() => live && setKnown([]));
    return () => {
      live = false;
    };
  }, []);
  if (!known) return <p className="faint">Loading…</p>;
  const how = (
    <details className="sync-how">
      <summary>How sync works</summary>
      <ul className="faint small-text">
        <li>The devices connect to each other directly over Wi-Fi. Nothing goes through a server.</li>
        <li>Keep this app open on both devices until the sync finishes. If a phone locks or switches apps, the sync stops.</li>
        <li>Before anything changes, both devices show exactly what will be added, updated, or deleted on each, and you confirm on either one.</li>
        <li>Every tree is synced. Each person, link, group, source, media item, to-do, and dismissed suggestion keeps its most recently edited copy, and so does each setting (except the camera option for sync codes). Deleting on one device deletes on the other. Media files are copied too.</li>
        <li>The first time, each device scans the other’s code. After that, one scan is usually enough.</li>
      </ul>
    </details>
  );
  const scan = (
    <button className="btn" onClick={onScan}>
      <Camera size={15} /> Scan code
    </button>
  );
  if (!known.length)
    return (
      <>
        <p>Copy your trees between this device and another one, such as your laptop and phone.</p>
        <div className="row wrap">
          <button className="btn primary" onClick={onOffer}>
            <QrCode size={15} /> Show code
          </button>
          {scan}
        </div>
        <p className="faint small-text">Choose “Show code” on one device and “Scan code” on the other. Both need this app open and the same Wi-Fi.</p>
        {how}
      </>
    );
  return (
    <>
      <div className="sync-known">
        <p className="field-label">Sync with</p>
        {known.map(([id, p, peer]) => (
          <button key={id} className="sync-device-btn" onClick={() => onReconnect(id, p.name || 'the other device')}>
            <RefreshCw size={20} />
            <span>
              <b>{p.name || 'Paired device'}</b>
              <small className="faint">{peer ? `Last synced ${ago(peer.at)}` : 'Paired, not synced yet'}</small>
            </span>
            <ChevronRight size={16} />
          </button>
        ))}
      </div>
      <div className="sync-other">
        <p className="faint small-text">Is the other device showing a code?</p>
        {scan}
      </div>
      <div className="sync-other">
        <p className="faint small-text">Another phone or computer?</p>
        <button className="btn ghost" onClick={onOffer}>
          <Plus size={15} /> Pair a new device
        </button>
      </div>
      {how}
    </>
  );
}

/** A QR code, with the same code as text for copying. */
function CodeBlock({ bytes }: { bytes: Uint8Array }) {
  const q = encodeQr(bytes);
  const n = q.size + 8;
  const text = signalToText(bytes);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      toast('Code copied. Paste it on the other device.', 'success');
    } catch {
      toast('Couldn’t copy. Select the code and copy it instead.', 'error');
    }
  };
  return (
    <div className="sync-code-block">
      <svg viewBox={`0 0 ${n} ${n}`} className="sync-qr" role="img" aria-label="Sync QR code" shapeRendering="crispEdges">
        <rect width={n} height={n} fill="#fff" />
        <path d={qrPath(q)} fill="#000" />
      </svg>
      <details>
        <summary>No camera? Use a text code</summary>
        <textarea className="textarea sync-code" rows={4} readOnly value={text} aria-label="Sync code" spellCheck={false} onFocus={(e) => e.currentTarget.select()} />
        <div className="row wrap">
          <button className="btn small" onClick={copy}>
            <Copy size={14} /> Copy code
          </button>
          {typeof navigator !== 'undefined' && 'share' in navigator && (
            <button className="btn small" onClick={() => navigator.share({ text }).catch(() => {})}>
              <Share size={14} /> Share code
            </button>
          )}
        </div>
      </details>
    </div>
  );
}

/** Camera scanner plus a paste box; calls onCode(bytes) once with a valid code of an expected kind. */
function CodeReader({
  scanLabel,
  expect,
  autoScan = false,
  primary = true,
  onCode,
  onError,
}: {
  scanLabel: string;
  expect: SignalType[];
  autoScan?: boolean;
  primary?: boolean;
  onCode: (bytes: Uint8Array) => Promise<void>;
  onError: (e: SyncError) => void;
}) {
  const [scanning, setScanning] = useState(autoScan && cameraSupported());
  const [error, setError] = useState('');
  const [paste, setPaste] = useState('');
  const busy = useRef(false);
  const accept = async (bytes: Uint8Array) => {
    if (busy.current) return;
    // A wrong or unreadable code (an old one, or this screen's own) is only a hint: scanning carries on.
    try {
      const sig = decodeSignal(bytes);
      if (!expect.includes(sig.type)) {
        setError(
          sig.type === 'answer'
            ? 'That’s a reply code. Scan the code on the other device’s screen.'
            : sig.type === 'addresses'
              ? 'That code goes the other way: scan it on the device that showed the first code.'
              : 'That’s a starting code, not a reply. Scan the code the other device shows back.',
        );
        return;
      }
    } catch (e) {
      setError((e as Error).message);
      return;
    }
    busy.current = true;
    setError('');
    try {
      await onCode(bytes);
    } catch (e) {
      onError(e as SyncError);
    }
  };
  return (
    <div className="stack tight">
      {scanning ? (
        <Scanner onBytes={accept} />
      ) : (
        cameraSupported() && (
          <div>
            <button className={`btn${primary ? ' primary' : ''}`} onClick={() => setScanning(true)}>
              <Camera size={15} /> {scanLabel}
            </button>
          </div>
        )
      )}
      <div className="sync-paste">
        <textarea
          className="textarea sync-code"
          rows={2}
          placeholder={CODE_PLACEHOLDER}
          aria-label="Paste a sync code"
          spellCheck={false}
          autoCapitalize="off"
          autoComplete="off"
          value={paste}
          onChange={(e) => setPaste(e.target.value)}
        />
        <button
          className="btn"
          onClick={() => {
            try {
              void accept(textToSignal(paste));
            } catch (e) {
              setError((e as Error).message);
            }
          }}
        >
          Use code
        </button>
      </div>
      {error && (
        <p className="sync-error small-text" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

/** Live camera view that calls onBytes for each QR code it sees, until it unmounts. */
function Scanner({ onBytes }: { onBytes: (bytes: Uint8Array) => void }) {
  const video = useRef<HTMLVideoElement>(null);
  const [msg, setMsg] = useState('Starting camera…');
  const [failed, setFailed] = useState(false);
  const cb = useRef(onBytes);
  cb.current = onBytes;
  useEffect(() => {
    let stream: MediaStream | null = null;
    let stopped = false;
    let t: ReturnType<typeof setTimeout> | undefined;
    (async () => {
      try {
        const qrLib = import('jsqr').then((m) => m.default);
        qrLib.catch(() => {}); // reported below, once the camera is running
        // The request stays pending, with no error, while a permission prompt waits: Firefox's next
        // to the address bar, or macOS's own, which can open behind other windows.
        const waiting = setTimeout(() => {
          if (!stopped)
            setMsg('Still waiting for the camera. Allow it in your browser’s prompt (near the address bar). On a Mac, also check System Settings → Privacy & Security → Camera. Or paste the code below instead.');
        }, 5000);
        try {
          stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: 'environment' } }, audio: false });
        } finally {
          clearTimeout(waiting);
        }
        const v = video.current;
        if (stopped || !v) return stream.getTracks().forEach((x) => x.stop());
        // Show the picture straight away; the decoder can finish loading meanwhile.
        v.srcObject = stream;
        await v.play().catch(() => {});
        let jsQR;
        try {
          jsQR = await qrLib;
        } catch {
          throw new Error('Couldn’t load the QR scanner. Paste the code instead.');
        }
        if (stopped) return;
        setMsg('Point the camera at the QR code.');
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
        const started = Date.now();
        let sawPicture = false;
        let warned = false;
        const tick = () => {
          if (stopped) return;
          const w = v.videoWidth;
          const h = v.videoHeight;
          if (w && h) {
            // Scale down large frames: decoding is faster and the code fills plenty of pixels.
            const k = Math.min(1, 720 / Math.max(w, h));
            canvas.width = Math.round(w * k);
            canvas.height = Math.round(h * k);
            ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
            const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
            if (!sawPicture) {
              for (let i = 0; i < img.data.length; i += 4 * 97)
                if (img.data[i] > 24 || img.data[i + 1] > 24 || img.data[i + 2] > 24) {
                  sawPicture = true;
                  break;
                }
            }
            const r = jsQR(img.data, img.width, img.height, { inversionAttempts: 'dontInvert' });
            if (r?.binaryData?.length) cb.current(Uint8Array.from(r.binaryData));
          }
          // A camera the system hasn't allowed (macOS Privacy settings, common with Firefox) gives a
          // stream of black frames rather than an error.
          if (!sawPicture && !warned && Date.now() - started > 4000) {
            warned = true;
            setMsg('The camera isn’t showing a picture. On a Mac, allow this browser in System Settings → Privacy & Security → Camera, then reload the page. Or paste the code instead.');
          }
          if (sawPicture && warned) {
            warned = false;
            setMsg('Point the camera at the QR code.');
          }
          t = setTimeout(tick, 150);
        };
        tick();
      } catch (e) {
        stream?.getTracks().forEach((x) => x.stop());
        if (stopped) return;
        setFailed(true);
        setMsg((e as Error)?.name === 'NotAllowedError' ? 'Camera permission was denied. Paste the code instead.' : (e as Error)?.message || 'Couldn’t start the camera. Paste the code instead.');
      }
    })();
    return () => {
      stopped = true;
      clearTimeout(t);
      stream?.getTracks().forEach((x) => x.stop());
    };
  }, []);
  return (
    <div className="stack tight">
      {!failed && (
        <div className="sync-scan">
          <video ref={video} playsInline muted autoPlay />
        </div>
      )}
      <p className="faint small-text">{msg}</p>
    </div>
  );
}

/** One device's column in the preview: each tree, then every item by kind. */
function PreviewSide({ title, side }: { title: string; side: SidePreview }) {
  return (
    <section className="sync-side">
      <h3>{title}</h3>
      {side.empty && <p className="faint small-text">No changes.</p>}
      {side.trees.map((t) => {
        if (t.kind === 'added')
          return (
            <p key={t.id} className="sync-tree sync-added">
              New tree “{t.name}” ({plural(t.people ?? 0, 'person', 'people')})
            </p>
          );
        if (t.kind === 'removed')
          return (
            <p key={t.id} className="sync-tree sync-removed">
              Tree “{t.name}” deleted
            </p>
          );
        return <TreeItems key={t.id} name={t.name} items={t.items} />;
      })}
      {side.settings.length > 0 && (
        <div className="sync-tree">
          <p>
            <b>Settings</b>
          </p>
          <ul>
            {side.settings.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function TreeItems({ name, items }: { name: string; items: ItemChange[] }) {
  const groups = (['added', 'updated', 'removed'] as const).map((kind) => [kind, items.filter((i) => i.kind === kind)] as const).filter(([, list]) => list.length);
  const label = { added: 'Added', updated: 'Updated', removed: 'Deleted' };
  return (
    <div className="sync-tree">
      <p>
        <b>“{name}”</b> <span className="faint">{groups.map(([kind, list]) => `${list.length} ${label[kind].toLowerCase()}`).join(' · ')}</span>
      </p>
      {groups.map(([kind, list]) => {
        const byType = new Map<string, ItemChange[]>();
        for (const it of list) byType.set(it.type, [...(byType.get(it.type) ?? []), it]);
        return (
          <details key={kind} className={`sync-group sync-${kind}`} open={list.length <= 8}>
            <summary>
              {label[kind]} ({list.length})
            </summary>
            {[...byType].map(([type, its]) => (
              <div key={type} className="sync-type">
                <p className="sync-type-name">
                  {TYPE_LABELS[type as ItemChange['type']]} ({its.length})
                </p>
                <ul>
                  {its.map((it, i) => (
                    <li key={i}>
                      {it.label}
                      {it.fields?.length ? <span className="faint"> — changed: {it.fields.join(', ')}</span> : null}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </details>
        );
      })}
    </div>
  );
}

function Details({ text }: { text: string }) {
  return (
    <details className="sync-how">
      <summary>Technical details</summary>
      <pre className="sync-details">{text}</pre>
      <button
        className="btn small"
        onClick={() =>
          navigator.clipboard.writeText(text).then(
            () => toast('Copied.'),
            () => toast('Couldn’t copy. Select the text instead.', 'error'),
          )
        }
      >
        <Copy size={14} /> Copy details
      </button>
    </details>
  );
}

/** "Technical details" for a waiting screen, read fresh each time it's opened. */
function LiveDetails({ session }: { session: SyncSession | null }) {
  const [text, setText] = useState('…');
  return (
    <details
      className="sync-how"
      onToggle={async (e) => {
        if (e.currentTarget.open && session) setText(await session.details());
      }}
    >
      <summary>Technical details</summary>
      <pre className="sync-details">{text}</pre>
    </details>
  );
}

/** "2 hours ago", "just now". */
export function ago(at: string, now = Date.now()): string {
  const s = Math.max(0, (now - new Date(at).getTime()) / 1000);
  if (s < 60) return 'just now';
  const units: [number, string][] = [
    [60, 'minute'],
    [3600, 'hour'],
    [86400, 'day'],
    [86400 * 30, 'month'],
    [86400 * 365, 'year'],
  ];
  let pick = units[0];
  for (const u of units) if (s >= u[0]) pick = u;
  return `${plural(Math.floor(s / pick[0]), pick[1])} ago`;
}

/** Settings: every device this one has synced or paired with, with "Forget", and the camera option. */
export function SyncSettings() {
  const camera = useApp((s) => !!s.settings.syncCameraForCodes);
  const [devices, setDevices] = useState<{ id: string; name: string; at?: string; oneScan: boolean }[]>([]);
  const load = () =>
    Promise.all([getPairs(), getPeers()])
      .then(([pairs, peers]) => {
        const ids = [...new Set([...Object.keys(peers), ...Object.keys(pairs)])];
        setDevices(
          ids
            .map((id) => ({ id, name: peers[id]?.name || pairs[id]?.name || 'Device', at: peers[id]?.at, oneScan: !!pairs[id] && pairs[id].canAnswer !== false }))
            .sort((a, b) => ((a.at ?? '') < (b.at ?? '') ? 1 : -1)),
        );
      })
      .catch(() => {});
  useEffect(() => {
    void load();
  }, []);
  return (
    <fieldset className="settings-group">
      <legend className="field-label">Sync with other devices</legend>
      <div className="stack tight small-text">
        <div className="row wrap">
          <button className="btn small" onClick={() => startSync()}>
            <RefreshCw size={14} /> Sync now
          </button>
        </div>
        {devices.length ? (
          <ul className="sync-devices">
            {devices.map((d) => (
              <li key={d.id}>
                <span className="grow">
                  <b>{d.name}</b>
                  <span className="faint"> · {d.at ? `last synced ${ago(d.at)}` : 'paired, not synced yet'}</span>
                  {!d.oneScan && <span className="faint"> · next sync needs a code from each device</span>}
                </span>
                <button
                  className="btn small ghost"
                  aria-label={`Forget ${d.name}`}
                  onClick={async () => {
                    await forgetDevice(d.id);
                    toast(`${d.name} forgotten. Pair with two codes to sync with it again.`);
                    void load();
                  }}
                >
                  Forget
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="faint">Not synced with another device yet.</p>
        )}
        <label className="mode-option">
          <input type="checkbox" checked={camera} onChange={(e) => updateSettings({ syncCameraForCodes: e.target.checked })} />
          <span>
            <b>One-scan syncing on any Wi-Fi</b>
            <small>
              Browsers only share this device’s network address while the page uses the camera, and some networks need it. With this on, the camera switches on for a moment while
              this device makes a one-scan code. Nothing is recorded. Without it, the other device may show a second code to scan back.
            </small>
          </span>
        </label>
      </div>
    </fieldset>
  );
}
